import React, { useCallback, useEffect, useState } from "react";
import { Alert, Button, Form, Modal, Table } from "react-bootstrap";

import { request } from "hooks/useOkoApi/useOkoApi";
import {
  SubjectValuePicker,
  clientLabel,
  subjectDisplay,
  userLabel,
} from "./SubjectValuePicker";

/**
 * Resolve the display labels for a set of targets' subject_values. Only
 * user/school/client carry opaque UUIDs that need a name lookup; the two enum
 * types are already human-readable. Batches by type, tolerates failures
 * (an unresolved id simply falls back to itself). Returns an {id -> label} map.
 */
const resolveTargetLabels = async (targets) => {
  const idsByType = { user: new Set(), school: new Set(), client: new Set() };
  targets.forEach((t) => {
    if (idsByType[t.subject_type]) idsByType[t.subject_type].add(t.subject_value);
  });

  const labels = {};
  const safe = async (fn) => {
    try {
      await fn();
    } catch (e) {
      /* leave those ids unresolved; they fall back to the raw value */
    }
  };

  await Promise.all([
    idsByType.user.size &&
      safe(async () => {
        const data = await request("admin/users?limit=200", { method: "GET" });
        (data?.users || []).forEach((u) => {
          if (idsByType.user.has(u.id)) labels[u.id] = userLabel(u);
        });
      }),
    idsByType.school.size &&
      safe(async () => {
        const data = await request("admin/schools", { method: "GET" });
        (data?.schools || []).forEach((s) => {
          if (idsByType.school.has(s.id)) labels[s.id] = s.name;
        });
      }),
    idsByType.client.size &&
      safe(async () => {
        const data = await request("admin/clients", { method: "GET" });
        (data?.clients || []).forEach((c) => {
          if (idsByType.client.has(c.id)) labels[c.id] = clientLabel(c);
        });
      }),
  ]);

  return labels;
};

const SUBJECT_TYPES = ["user", "school", "client", "client_type", "educator_type"];

const EMPTY_FLAG_FORM = {
  key: "",
  description: "",
  default_enabled: false,
  is_active: true,
  superuser_override: false,
};

const EMPTY_TARGET_FORM = {
  subject_type: "client",
  subject_value: "",
  enabled: true,
};

/**
 * Feature Flags tab — list + create flags, toggle active/default, and manage a
 * flag's targets. A target is what actually turns a flag on for a subset:
 * (subject_type ∈ user/school/client/client_type/educator_type, subject_value,
 * enabled). Reads/writes the superuser-guarded /admin/feature-flags endpoints.
 */
export const FlagsTab = () => {
  const [flags, setFlags] = useState([]);
  const [form, setForm] = useState(EMPTY_FLAG_FORM);
  const [error, setError] = useState(null);

  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY_FLAG_FORM);
  const [editError, setEditError] = useState(null);

  const [managing, setManaging] = useState(null);
  const [targets, setTargets] = useState([]);
  const [labelsById, setLabelsById] = useState({});
  const [targetForm, setTargetForm] = useState(EMPTY_TARGET_FORM);
  const [targetError, setTargetError] = useState(null);

  const loadFlags = useCallback(async () => {
    try {
      const data = await request("admin/feature-flags/flags", {
        method: "GET",
      });
      setFlags(data?.flags || []);
    } catch (e) {
      setError(e?.detail || "Failed to load flags");
    }
  }, []);

  useEffect(() => {
    loadFlags();
  }, [loadFlags]);

  const setField = (name) => (event) => {
    const { value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const setCheck = (name) => (event) => {
    const { checked } = event.target;
    setForm((prev) => ({ ...prev, [name]: checked }));
  };

  const submit = async (event) => {
    event.preventDefault();
    setError(null);
    const payload = {
      key: form.key,
      description: form.description || null,
      default_enabled: form.default_enabled,
      is_active: form.is_active,
      superuser_override: form.superuser_override,
    };
    try {
      await request("admin/feature-flags/flags", { method: "POST", payload });
      setForm(EMPTY_FLAG_FORM);
      await loadFlags();
    } catch (e) {
      setError(e?.detail || "Failed to create flag");
    }
  };

  const patchFlag = async (flag, changes) => {
    setError(null);
    try {
      await request(`admin/feature-flags/flags/${flag.id}`, {
        method: "PATCH",
        payload: changes,
      });
      await loadFlags();
    } catch (e) {
      setError(e?.detail || "Failed to update flag");
    }
  };

  const openEdit = (flag) => {
    setEditError(null);
    setEditForm({
      key: flag.key,
      description: flag.description || "",
      default_enabled: flag.default_enabled,
      is_active: flag.is_active,
      superuser_override: flag.superuser_override,
    });
    setEditing(flag);
  };

  const closeEdit = () => {
    setEditing(null);
    setEditError(null);
  };

  const setEditCheck = (name) => (event) => {
    const { checked } = event.target;
    setEditForm((prev) => ({ ...prev, [name]: checked }));
  };

  // The key is NOT patchable: the resolver and every caller address a flag by
  // it, so the field is shown read-only rather than left out, which would read
  // as "this modal does not know which flag it is editing".
  const saveEdit = async (event) => {
    event.preventDefault();
    setEditError(null);
    try {
      await request(`admin/feature-flags/flags/${editing.id}`, {
        method: "PATCH",
        payload: {
          description: editForm.description || null,
          default_enabled: editForm.default_enabled,
          is_active: editForm.is_active,
          superuser_override: editForm.superuser_override,
        },
      });
      await loadFlags();
      closeEdit();
    } catch (e) {
      setEditError(e?.detail || "Failed to update flag");
    }
  };

  const loadTargets = useCallback(async (flag) => {
    try {
      const data = await request(
        `admin/feature-flags/flags/${flag.id}/targets`,
        { method: "GET" }
      );
      const rows = data?.targets || [];
      setTargets(rows);
      setLabelsById(await resolveTargetLabels(rows));
    } catch (e) {
      setTargetError(e?.detail || "Failed to load targets");
    }
  }, []);

  const openManage = async (flag) => {
    setManaging(flag);
    setTargets([]);
    setLabelsById({});
    setTargetForm(EMPTY_TARGET_FORM);
    setTargetError(null);
    await loadTargets(flag);
  };

  const closeManage = () => {
    setManaging(null);
    setTargets([]);
    setLabelsById({});
  };

  const submitTarget = async (event) => {
    event.preventDefault();
    setTargetError(null);
    const payload = {
      subject_type: targetForm.subject_type,
      subject_value: targetForm.subject_value,
      enabled: targetForm.enabled,
    };
    try {
      await request(`admin/feature-flags/flags/${managing.id}/targets`, {
        method: "POST",
        payload,
      });
      setTargetForm(EMPTY_TARGET_FORM);
      await loadTargets(managing);
    } catch (e) {
      setTargetError(e?.detail || "Failed to add target");
    }
  };

  const deleteTarget = async (target) => {
    setTargetError(null);
    try {
      await request(`admin/feature-flags/targets/${target.id}`, {
        method: "DELETE",
      });
      await loadTargets(managing);
    } catch (e) {
      setTargetError(e?.detail || "Failed to delete target");
    }
  };

  return (
    <div>
      {error && <Alert variant="danger">{error}</Alert>}
      <Form onSubmit={submit}>
        <Form.Group className="mb-3" controlId="key">
          <Form.Label>Key</Form.Label>
          <Form.Control
            value={form.key}
            placeholder="e.g. intelligent_grouping"
            onChange={setField("key")}
          />
        </Form.Group>
        <Form.Group className="mb-3" controlId="description">
          <Form.Label>Description</Form.Label>
          <Form.Control
            value={form.description}
            onChange={setField("description")}
          />
        </Form.Group>
        <Form.Group className="mb-3" controlId="default_enabled">
          <Form.Check
            type="checkbox"
            label="Default enabled (on for everyone with no matching target)"
            checked={form.default_enabled}
            onChange={setCheck("default_enabled")}
          />
        </Form.Group>
        <Form.Group className="mb-3" controlId="is_active">
          <Form.Check
            type="checkbox"
            label="Active (unchecking is a global kill-switch)"
            checked={form.is_active}
            onChange={setCheck("is_active")}
          />
        </Form.Group>
        <Form.Group className="mb-4" controlId="superuser_override">
          <Form.Check
            type="checkbox"
            label="Available for super users (forced on while a superuser impersonates a teacher — overrides that teacher's targeting)"
            checked={form.superuser_override}
            onChange={setCheck("superuser_override")}
          />
        </Form.Group>
        <Button type="submit">Create flag</Button>
      </Form>

      <Table striped bordered hover style={{ marginTop: "32px" }}>
        <thead>
          <tr>
            <th>Key</th>
            <th>Description</th>
            <th>Default</th>
            <th>Active</th>
            <th>Super users</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {flags.map((flag) => (
            <tr key={flag.id}>
              <td>{flag.key}</td>
              <td>{flag.description}</td>
              <td>{String(flag.default_enabled)}</td>
              <td>{String(flag.is_active)}</td>
              <td>{String(flag.superuser_override)}</td>
              <td>
                <Button
                  size="sm"
                  variant="outline-primary"
                  onClick={() => openManage(flag)}
                >
                  Manage targets
                </Button>{" "}
                <Button
                  size="sm"
                  variant="outline-primary"
                  onClick={() => openEdit(flag)}
                >
                  Edit
                </Button>{" "}
                <Button
                  size="sm"
                  variant="outline-secondary"
                  onClick={() =>
                    patchFlag(flag, { is_active: !flag.is_active })
                  }
                >
                  {flag.is_active ? "Deactivate" : "Activate"}
                </Button>{" "}
                <Button
                  size="sm"
                  variant="outline-secondary"
                  onClick={() =>
                    patchFlag(flag, {
                      default_enabled: !flag.default_enabled,
                    })
                  }
                >
                  {flag.default_enabled
                    ? "Default off"
                    : "Default on"}
                </Button>{" "}
                <Button
                  size="sm"
                  variant="outline-secondary"
                  onClick={() =>
                    patchFlag(flag, {
                      superuser_override: !flag.superuser_override,
                    })
                  }
                >
                  {flag.superuser_override ? "Superuser off" : "Superuser on"}
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      <Modal show={editing !== null} onHide={closeEdit}>
        <Modal.Header closeButton>
          <Modal.Title>Edit flag — {editing?.key}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={saveEdit}>
          <Modal.Body>
            {editError && <Alert variant="danger">{editError}</Alert>}

            <Form.Group className="mb-3" controlId="edit_key">
              <Form.Label>Key</Form.Label>
              <Form.Control type="text" value={editForm.key} readOnly disabled />
              <Form.Text className="text-muted">
                The key cannot change — every caller addresses the flag by it.
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3" controlId="edit_description">
              <Form.Label>Description</Form.Label>
              <Form.Control
                type="text"
                value={editForm.description}
                onChange={(event) =>
                  setEditForm((prev) => ({
                    ...prev,
                    description: event.target.value,
                  }))
                }
              />
            </Form.Group>

            <Form.Group className="mb-3" controlId="edit_default_enabled">
              <Form.Check
                type="checkbox"
                label="Default enabled (on for everyone with no matching target)"
                checked={editForm.default_enabled}
                onChange={setEditCheck("default_enabled")}
              />
            </Form.Group>

            <Form.Group className="mb-3" controlId="edit_is_active">
              <Form.Check
                type="checkbox"
                label="Active (unchecking is a global kill-switch)"
                checked={editForm.is_active}
                onChange={setEditCheck("is_active")}
              />
            </Form.Group>

            <Form.Group className="mb-3" controlId="edit_superuser_override">
              <Form.Check
                type="checkbox"
                label={
                  "Available for super users (forced on while a superuser " +
                  "impersonates a teacher — overrides that teacher's targeting)"
                }
                checked={editForm.superuser_override}
                onChange={setEditCheck("superuser_override")}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={closeEdit}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              Save changes
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal show={managing !== null} onHide={closeManage} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            Targets — {managing?.key}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {targetError && <Alert variant="danger">{targetError}</Alert>}
          {targets.length === 0 && (
            <p className="text-muted">
              No targets. With none, this flag resolves to its default for
              everyone.
            </p>
          )}
          {targets.length > 0 && (
            <Table striped bordered size="sm">
              <thead>
                <tr>
                  <th>Subject type</th>
                  <th>Subject</th>
                  <th>Enabled</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {targets.map((target) => (
                  <tr key={target.id}>
                    <td>{target.subject_type}</td>
                    <td>
                      {subjectDisplay(
                        target.subject_type,
                        target.subject_value,
                        labelsById
                      )}
                    </td>
                    <td>{String(target.enabled)}</td>
                    <td>
                      <Button
                        size="sm"
                        variant="outline-danger"
                        onClick={() => deleteTarget(target)}
                      >
                        Remove
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}

          <Form onSubmit={submitTarget} style={{ marginTop: "24px" }}>
            <Form.Group className="mb-3" controlId="subject_type">
              <Form.Label>Subject type</Form.Label>
              <Form.Control
                as="select"
                value={targetForm.subject_type}
                onChange={(event) => {
                  const { value } = event.target;
                  setTargetForm((prev) => ({
                    ...prev,
                    subject_type: value,
                    subject_value: "",
                  }));
                }}
              >
                {SUBJECT_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </Form.Control>
            </Form.Group>
            <Form.Group className="mb-3" controlId="subject_value">
              <Form.Label>Subject value</Form.Label>
              <SubjectValuePicker
                subjectType={targetForm.subject_type}
                value={targetForm.subject_value}
                onChange={(subjectValue) =>
                  setTargetForm((prev) => ({
                    ...prev,
                    subject_value: subjectValue,
                  }))
                }
              />
            </Form.Group>
            <Form.Group className="mb-3" controlId="target_enabled">
              <Form.Check
                type="checkbox"
                label="Enabled (uncheck to explicitly turn OFF for this subject)"
                checked={targetForm.enabled}
                onChange={(event) =>
                  setTargetForm((prev) => ({
                    ...prev,
                    enabled: event.target.checked,
                  }))
                }
              />
            </Form.Group>
            <Button type="submit" variant="primary">
              Add target
            </Button>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeManage}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};
